import { useState, useEffect, useContext, useMemo, useCallback, useRef } from 'react';
import {
  FiPlus, FiEdit2, FiTrash2, FiClock, FiCalendar,
  FiAlertTriangle, FiUsers, FiX, FiToggleLeft, FiToggleRight, FiCheck, FiXCircle, FiFilter, FiChevronUp, FiChevronDown, FiVideo, FiUpload, FiDownload, FiCheck as FiCheckIcon, FiX as FiXIcon, FiChevronLeft, FiChevronRight
} from 'react-icons/fi';
import { FaCheckCircle, FaTimesCircle, FaUserCheck } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { formatTime, formatDate as formatDateUtil, formatDateLocale } from '../../utils/dateFormatter';
import UserContext from '../../Components/UserContext';
import HttpInterceptor from '../../lib/HttpInterceptor';
import { formatName } from '../../utils/dataTransformers';
import TimePicker from '../../Components/TimePicker';
import ConfirmDialog from '../../Components/ConfirmDialog';

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const CLASS_DURATION_OPTIONS = ['30 mins', '45 mins', '1 hour', '1.5 hours', '2 hours'];
const MAX_FREE_STUDENTS = 10;

function Classes() {
  const { session } = useContext(UserContext);
  const editFormRef = useRef(null);
  const [isPWA, setIsPWA] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  
  const isValidObjectId = (id) => {
    if (!id || typeof id !== 'string') return false;
    return /^[0-9a-fA-F]{24}$/.test(id);
  };
  
  // Use trainerId if available (for business partners), otherwise use session id
  // Only use valid ObjectIds
  const trainerId = useMemo(() => {
    const id = session?.trainerId || session?.id;
    if (!id || !isValidObjectId(id)) {
      console.warn('Invalid trainer ID format:', id);
      return null;
    }
    return id;
  }, [session?.trainerId, session?.id]);
  
  // Track if we've already shown the "no programs" warning to avoid duplicates
  const noProgramsWarningShownRef = useRef(false);
  
  const [state, setState] = useState({
    userPrograms: [],
    classes: [],
    newClass: {
      day: 'Monday',
      time: '',
      classType: '',
      classDuration: '1 hour',
      maxStudents: MAX_FREE_STUDENTS,
      repeat: false,
      repeatDays: [],
      repeatUntil: '',
      programId: '',
      isActive: true,
      whatsappLink: '',
      materials: [],
      level: '',
      description: '',
      meetingLink: '',
      meetingLinkType: 'manual'
    },
    filters: {
      day: 'All',
      program: 'All'
    },
    ui: {
      showProModal: false,
      showAddClass: false,
      showDeleteConfirm: null,
      showEditConfirm: false,
        showEditFormModal: false,
      isEditing: null,
        editingClassData: null, // Store the class data being edited
        editingRelatedClasses: [], // Store related class IDs when editing repeat weekly classes
      isLoading: false,
      isFetchingClasses: false,
      showFilters: false,
      showStudentsModal: null,
      showAttendanceModal: null,
      showRecordingsModal: null,
      classStudents: [],
      classEnrollmentTotal: 0,
      classEnrollmentVisible: 0,
      classAttendance: [],
        classRecordings: [],
        attendanceSessionDate: null,
      showDeleteRecordingConfirm: null
    },
    dateTimeSettings: {
      dateFormat: 'MM/DD/YYYY',
      timeFormat: '12-hour'
    },
    sorting: {
    field: 'day', // Default to sorting by day
    direction: 'asc' // 'asc' or 'desc'
    },
    trainerSubscriptionPlan: 'free',
    advanceFeatures: { increasedClassCapacity: false }
  });

  const activePrograms = useMemo(() => 
    state.userPrograms.filter(p => p.isActive), 
    [state.userPrograms]
  );

  const handleSort = (field) => {
    setState(prev => ({
      ...prev,
      sorting: {
        field,
        direction: prev.sorting.field === field && prev.sorting.direction === 'asc' ? 'desc' : 'asc'
      }
    }));
  };

  const filteredClasses = useMemo(() => {
    let result = state.classes.filter(c => {
      const programMatch = state.filters.program === 'All' || 
                        (c.programId?._id || c.programId) === state.filters.program;
      const dayMatch = state.filters.day === 'All' || c.day === state.filters.day;
      return programMatch && dayMatch;
    });

    // Apply sorting if a field is selected
    if (state.sorting.field) {
      result.sort((a, b) => {
        let valueA, valueB;

        switch (state.sorting.field) {
          case 'classType':
            valueA = a.classType.toLowerCase();
            valueB = b.classType.toLowerCase();
            break;
          case 'program':
            const programA = state.userPrograms.find(p => p._id === (a.programId?._id || a.programId))?.name || '';
            const programB = state.userPrograms.find(p => p._id === (b.programId?._id || b.programId))?.name || '';
            valueA = programA.toLowerCase();
            valueB = programB.toLowerCase();
            break;
          case 'day':
            // Sort by day order (Monday to Sunday)
            const dayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
            valueA = dayOrder.indexOf(a.day);
            valueB = dayOrder.indexOf(b.day);
            break;
          case 'time':
            valueA = a.time;
            valueB = b.time;
            break;
          default:
            return 0;
        }

        if (valueA < valueB) {
          return state.sorting.direction === 'asc' ? -1 : 1;
        }
        if (valueA > valueB) {
          return state.sorting.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return result;
  }, [state.classes, state.filters, state.sorting, state.userPrograms]);

  // Calendar view helper functions
  const changeMonth = (increment) => {
    const newMonth = currentMonth + increment;
    if (newMonth < 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else if (newMonth > 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(newMonth);
    }
  };

  const getDayOfWeek = (day, month, year) => {
    const date = new Date(year, month, day);
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return dayNames[date.getDay()];
  };

  const hasClassesOnDay = (day, month, year) => {
    if (!day) return false;
    const dayName = getDayOfWeek(day, month, year);
    return filteredClasses.some(classItem => classItem.day === dayName);
  };

  const getClassesForDate = (day, month, year) => {
    if (!day) return [];
    const dayName = getDayOfWeek(day, month, year);
    return filteredClasses.filter(classItem => classItem.day === dayName);
  };

  // Calendar View Component
  const CalendarView = () => {
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();
    
    const days = [];
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }

    return (
      <div className={`${isPWA ? 'bg-white/95 backdrop-blur-sm border-2 border-orange-200/50 shadow-xl' : 'bg-white rounded-lg shadow-sm'} rounded-lg p-6`}>
        <div className={`flex ${isPWA ? 'flex-col sm:flex-row' : ''} justify-between items-center ${isPWA ? 'mb-4 sm:mb-4' : 'mb-4'} ${isPWA ? 'gap-3' : ''}`}>
          <h2 className={`${isPWA ? 'text-lg sm:text-xl' : 'text-xl'} font-semibold ${isPWA ? 'text-orange-800' : ''}`}>Calendar View</h2>
          <div className={`flex items-center ${isPWA ? 'gap-2' : 'space-x-2'}`}>
            <button
              onClick={() => changeMonth(-1)}
              className={`p-2 ${isPWA ? 'bg-orange-50 hover:bg-orange-100 border border-orange-200/50 text-orange-700 shadow-sm' : 'hover:bg-gray-100'} rounded transition-all ${isPWA ? 'font-semibold' : ''}`}
            >
              <FiChevronLeft className="w-5 h-5" />
            </button>
            <span className={`font-medium ${isPWA ? 'text-orange-800' : ''}`}>
              {formatDateLocale(new Date(currentYear, currentMonth).toISOString(), null, { 
                month: 'long', 
                year: 'numeric' 
              })}
            </span>
            <button
              onClick={() => changeMonth(1)}
              className={`p-2 ${isPWA ? 'bg-orange-50 hover:bg-orange-100 border border-orange-200/50 text-orange-700 shadow-sm' : 'hover:bg-gray-100'} rounded transition-all ${isPWA ? 'font-semibold' : ''}`}
            >
              <FiChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        <div className="grid grid-cols-7 gap-1">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className={`p-2 text-center text-sm font-medium ${isPWA ? 'text-orange-700' : 'text-gray-500'}`}>
              {day}
            </div>
          ))}
          
          {days.map((day, index) => (
            <div
              key={index}
              className={`p-2 min-h-[60px] ${isPWA ? 'border-2' : 'border'} ${isPWA ? 'border-orange-200/50' : 'border-gray-200'} ${
                day ? (isPWA ? 'cursor-pointer hover:bg-orange-50 hover:border-orange-300/70' : 'cursor-pointer hover:bg-gray-50') : (isPWA ? 'bg-orange-50/30' : 'bg-gray-100')
              } transition-all`}
              onClick={() => day && setSelectedDate({ day, month: currentMonth, year: currentYear })}
            >
              {day && (
                <>
                  <div className={`text-sm font-medium ${isPWA ? 'text-orange-800' : ''}`}>{day}</div>
                  {hasClassesOnDay(day, currentMonth, currentYear) && (
                    <div className={`w-2 h-2 ${isPWA ? 'bg-orange-500' : 'bg-orange-500'} rounded-full mx-auto mt-1`}></div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Date Detail View Component
  const DateDetailView = ({ day, month, year, classes, onBack }) => {
    const dateStr = formatDateLocale(new Date(year, month, day).toISOString(), null, { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });

    return (
      <div className={`${isPWA ? 'bg-white/95 backdrop-blur-sm border-2 border-orange-200/50 shadow-xl' : 'bg-white rounded-lg shadow-sm'} rounded-lg p-6`}>
        <div className="flex items-center mb-4">
          <button onClick={onBack} className={`mr-3 p-2 ${isPWA ? 'hover:bg-orange-100 border border-orange-200/50' : 'hover:bg-gray-100'} rounded`}>
            <FiChevronLeft className="w-5 h-5" />
          </button>
          <h2 className={`${isPWA ? 'text-lg sm:text-xl font-bold text-orange-800' : 'text-xl font-semibold'}`}>{dateStr}</h2>
        </div>
        
        {classes.length === 0 ? (
          <p className={`text-center ${isPWA ? 'text-orange-600' : 'text-gray-500'} py-8`}>No classes scheduled for this day</p>
        ) : (
          <div className="space-y-4">
            {classes.map((classItem) => {
              const program = state.userPrograms.find(p => p._id === (classItem.programId?._id || classItem.programId));
              const enrolledCount = classItem.enrolledCount !== undefined 
                ? classItem.enrolledCount 
                : (classItem.enrolledStudents?.length || 0);
              const isAdvancePlan = state.trainerSubscriptionPlan === 'advance';
              const hasIncreasedCapacity = isAdvancePlan && state.advanceFeatures?.increasedClassCapacity;
              const maxAllowed = hasIncreasedCapacity ? 50 : MAX_FREE_STUDENTS;
              const displayMax = maxAllowed;

              return (
                <div 
                  key={classItem._id}
                  className={`${isPWA ? 'bg-gradient-to-br from-white to-orange-50/20 border-2 border-orange-200/50 shadow-lg' : 'bg-white border border-gray-200 rounded-lg shadow-sm'} ${isPWA ? 'rounded-xl' : 'rounded-lg'} p-4`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className={`${isPWA ? 'text-base sm:text-lg font-bold text-orange-800' : 'text-lg font-semibold'}`}>
                        {classItem.classType}
                      </h3>
                      {program && (
                        <p className={`text-sm ${isPWA ? 'text-orange-600' : 'text-gray-600'} mt-1`}>{program.name}</p>
                      )}
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded ${
                      classItem.isActive 
                        ? (isPWA ? 'bg-green-100 text-green-800 border border-green-200' : 'bg-green-100 text-green-800')
                        : (isPWA ? 'bg-gray-100 text-gray-800 border border-gray-200' : 'bg-gray-100 text-gray-800')
                    }`}>
                      {classItem.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  
                  <div className={`flex flex-wrap gap-4 ${isPWA ? 'text-sm text-orange-700' : 'text-sm text-gray-600'} mt-3`}>
                    <div className="flex items-center">
                      <FiCalendar className="mr-1" />
                      <span>{classItem.day}</span>
                    </div>
                    <div className="flex items-center">
                      <FiClock className="mr-1" />
                      <span>{formatTime(classItem.time, state.dateTimeSettings.timeFormat)}</span>
                    </div>
                    <div className="flex items-center">
                      <span>Duration: {classItem.classDuration || '1 hour'}</span>
                    </div>
                    <div className="flex items-center">
                      <FiUsers className="mr-1" />
                      <span>Enrolled: {enrolledCount} / {displayMax}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Track if settings fetch is in progress to prevent duplicate calls
  const isFetchingSettingsRef = useRef(false);

  const fetchSettings = useCallback(async () => {
    // Prevent concurrent calls
    if (isFetchingSettingsRef.current) {
      return;
    }
    
    try {
      isFetchingSettingsRef.current = true;
      const response = await HttpInterceptor.get('/api/trainer-settings', {
        showErrorToast: false // Suppress error toasts for settings
      });
      setState(prev => ({
        ...prev,
        dateTimeSettings: {
          dateFormat: response.data.data.dateFormat || 'MM/DD/YYYY',
          timeFormat: response.data.data.timeFormat || '12-hour'
        }
      }));
    } catch (err) {
      // Only log error if it's not a network error (backend might be down)
      if (err.code !== 'ERR_NETWORK' && err.code !== 'ERR_CONNECTION_REFUSED') {
      console.error('Failed to fetch settings:', err);
      }
      // Silently fail for network errors to avoid spam
    } finally {
      isFetchingSettingsRef.current = false;
    }
  }, []);

  const fetchClasses = useCallback(async (userPrograms, programFilter, trainerId) => {
    if (!isValidObjectId(trainerId) || !userPrograms || userPrograms.length === 0) {
      isFetchingRef.current = false;
      return;
    }
    
    try {
      setState(prev => ({ 
        ...prev, 
        ui: { ...prev.ui, isFetchingClasses: true, isLoading: true } 
      }));

      let allClasses = [];

      if (programFilter === 'All') {
        const responses = await Promise.all(
          userPrograms.map(program => 
            HttpInterceptor.get('/api/classes', {
              params: { trainerId, programId: program._id }
            })
          )
        );
        allClasses = responses.flatMap(response => Array.isArray(response.data) ? response.data : []);
      } else {
        const response = await HttpInterceptor.get('/api/classes', {
          params: { trainerId, programId: programFilter }
        });
        allClasses = Array.isArray(response.data) ? response.data : [];
      }

      setState(prev => ({
        ...prev,
        classes: allClasses,
        ui: {
          ...prev.ui,
          isFetchingClasses: false,
          isLoading: false
        }
      }));
    } catch (error) {
      const errorMessage = error.response?.data?.message === 'Program ID is required'
        ? 'Please select a program to view classes'
        : error.response?.data?.message || 'Failed to fetch classes. Please try again.';
      toast.error(errorMessage);
      console.error('Fetch classes error:', error);
      setState(prev => ({ 
        ...prev, 
        classes: [],
        ui: {
          ...prev.ui,
          isFetchingClasses: false,
          isLoading: false
        }
      }));
    } finally {
      isFetchingRef.current = false;
    }
  }, []);

  const fetchPrograms = useCallback(async () => {
    if (!trainerId) {
      console.warn('Cannot fetch programs: Invalid trainer ID');
      return;
    }
    // Check if user is an approved business partner
    if (!session?.isTrainer || session?.trainerStatus !== 'active') {
      console.warn('User is not an approved business partner');
      return;
    }

    try {
      setState(prev => ({ ...prev, ui: { ...prev.ui, isLoading: true } }));

      const response = await HttpInterceptor.get('/api/programs', {
        params: { trainerId: trainerId }
      });

      const firstActiveProgram = response.data.find(p => p.isActive)?._id || 
                               (response.data[0]?._id || '');

      const hasPrograms = response.data.length > 0;
      
      setState(prev => {
        const previousProgramCount = prev.userPrograms.length;
        const wasEmpty = previousProgramCount === 0;
        
        // Only show warning once when programs change from having programs to having none
        // or when first loading and there are no programs
        if (!hasPrograms && (!noProgramsWarningShownRef.current || !wasEmpty)) {
          noProgramsWarningShownRef.current = true;
          // Use setTimeout to avoid showing toast during state update
          setTimeout(() => {
            toast.warning('No programs available. Please create a program to add classes.');
          }, 0);
        } else if (hasPrograms) {
          // Reset the flag if programs are available
          noProgramsWarningShownRef.current = false;
        }
        
        return {
          ...prev,
          userPrograms: response.data,
          newClass: {
            ...prev.newClass,
            programId: firstActiveProgram
          },
          ui: {
            ...prev.ui,
            isLoading: false
          }
        };
      });
    } catch (error) {
      // Only show error if it's not a network error (backend might be down)
      if (error.code !== 'ERR_NETWORK' && error.code !== 'ERR_CONNECTION_REFUSED') {
      const errorMessage = error.response?.data?.message || 'Failed to fetch programs';
      toast.error(errorMessage);
      console.error('Fetch programs error:', error.response || error);
      } else {
        // Silently handle network errors to avoid spam
        console.warn('Network error fetching programs - backend may be down');
      }
      setState(prev => ({ ...prev, ui: { ...prev.ui, isLoading: false } }));
    }
  }, [trainerId, session?.isTrainer, session?.trainerStatus]);

  // Track if we've fetched programs for the current trainerId to prevent duplicate fetches
  const fetchedProgramsRef = useRef(false);
  const lastTrainerIdRef = useRef(null);
  
  // Reset flags when trainerId changes (new user/login)
  // Detect PWA mode
  useEffect(() => {
    const checkPWA = () => {
      const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                         window.navigator.standalone === true;
      setIsPWA(standalone);
    };
    checkPWA();
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handleChange = () => checkPWA();
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    if (lastTrainerIdRef.current !== trainerId) {
      lastTrainerIdRef.current = trainerId;
      fetchedProgramsRef.current = false;
      noProgramsWarningShownRef.current = false;
    }
  }, [trainerId]);
  
  // Fetch trainer profile to get subscription plan
  const fetchTrainerProfile = useCallback(async () => {
    try {
      const response = await HttpInterceptor.get('/api/trainer/profile', {
        showErrorToast: false
      });
      const profileData = response.data?.data || response.data || {};
      setState(prev => ({
        ...prev,
        trainerSubscriptionPlan: profileData.subscriptionPlan || 'free',
        advanceFeatures: profileData.advanceFeatures || { increasedClassCapacity: false }
      }));
    } catch (err) {
      console.log('Could not fetch trainer profile, using default subscription plan');
    }
  }, []);

  useEffect(() => {
    if (!trainerId) return;
    
    // Only fetch once per trainerId - use ref to prevent duplicate calls
    if (!fetchedProgramsRef.current) {
      fetchedProgramsRef.current = true;
      // Call functions directly - they're stable enough with their own dependencies
      fetchPrograms();
      fetchSettings();
      fetchTrainerProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trainerId]); // Intentionally exclude fetchPrograms/fetchSettings to prevent re-runs

  // Refresh settings on focus (debounced) - removed interval to reduce API calls
  useEffect(() => {
    if (!trainerId) return;

    let focusTimeout;
    const handleFocus = () => {
      // Debounce focus events to prevent multiple rapid calls
      clearTimeout(focusTimeout);
      focusTimeout = setTimeout(() => {
      fetchSettings();
      }, 1000); // Wait 1 second after focus before fetching
    };
    
    window.addEventListener('focus', handleFocus);

    return () => {
      clearTimeout(focusTimeout);
      window.removeEventListener('focus', handleFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trainerId]); // Exclude fetchSettings to prevent re-creating listener

  // Track if we're currently fetching to prevent duplicate calls
  const isFetchingRef = useRef(false);
  const lastProgramsLengthRef = useRef(0);
  const lastProgramFilterRef = useRef(null);
  const lastTrainerIdForFetchRef = useRef(null);

  useEffect(() => {
    const programsLength = state.userPrograms.length;
    const programFilter = state.filters.program;
    
    // Only fetch if:
    // 1. We have programs
    // 2. Not currently fetching
    // 3. Programs length, filter, or trainerId actually changed
    // 4. Valid trainerId
    const hasChanged = 
      programsLength !== lastProgramsLengthRef.current || 
      programFilter !== lastProgramFilterRef.current ||
      trainerId !== lastTrainerIdForFetchRef.current;
    
    if (
      programsLength > 0 && 
      !isFetchingRef.current && 
      trainerId &&
      hasChanged
    ) {
      lastProgramsLengthRef.current = programsLength;
      lastProgramFilterRef.current = programFilter;
      lastTrainerIdForFetchRef.current = trainerId;
      isFetchingRef.current = true;
      fetchClasses(state.userPrograms, programFilter, trainerId).finally(() => {
        isFetchingRef.current = false;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.filters.program, state.userPrograms.length, trainerId]); // Use length instead of array to avoid reference issues



  const handleClassTypeChange = (e) => {
    setState(prev => ({
      ...prev,
      newClass: {
        ...prev.newClass,
        classType: e.target.value
      }
    }));
  };

  // Memoize time change handler to prevent infinite loops in TimePicker
  const handleTimeChange = useCallback((time) => {
    setState(prev => {
      // Only update if time actually changed
      if (prev.newClass.time === time) {
        return prev;
      }
      return {
        ...prev,
        newClass: {
          ...prev.newClass,
          time: time
        }
      };
    });
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setState(prev => ({
      ...prev,
      newClass: {
        ...prev.newClass,
        [name]: value
      }
    }));
  };



  const handleMaxStudentsChange = (e) => {
    const value = e.target.value;
    const numValue = parseInt(value) || 0;
    // Trigger upgrade prompt at 11th student (free tier allows up to 10)
    if (numValue > MAX_FREE_STUDENTS) {
      setState(prev => ({ ...prev, ui: { ...prev.ui, showProModal: true } }));
      return;
    }
    setState(prev => ({
      ...prev,
      newClass: {
        ...prev.newClass,
        maxStudents: numValue
      }
    }));
  };

  const checkForDuplicateClass = () => {
    const { day, time, classType, programId, repeat, repeatDays } = state.newClass;
    
    // Check for duplicate in single class mode
    if (!repeat) {
      return state.classes.some(c => 
        c.day === day && 
        c.time === time && 
        c.classType === classType && 
        (c.programId?._id || c.programId) === programId
      );
    }
    
    // Check for duplicates in repeat mode
    if (repeat && repeatDays.length > 0) {
      return repeatDays.some(day => 
        state.classes.some(c => 
          c.day === day && 
          c.time === time && 
          c.classType === classType && 
          (c.programId?._id || c.programId) === programId
        )
      );
    }
    
    return false;
  };

  const handleAddOrUpdateClass = async () => {
    if (!state.newClass.time || !state.newClass.classType || !state.newClass.programId) {
      toast.warning('Please fill all required fields');
      return;
    }

    if (state.newClass.repeat && state.newClass.repeatDays.length === 0) {
      toast.warning('Please select at least one day for repeating classes');
      return;
    }

    // Check for duplicate classes
    if (checkForDuplicateClass()) {
      toast.error('A class with the same day, time, type and program already exists');
      return;
    }

    try {
      setState(prev => ({ ...prev, ui: { ...prev.ui, isLoading: true } }));

      const basePayload = {
        time: state.newClass.time,
        classType: state.newClass.classType,
        classDuration: state.newClass.classDuration,
        maxStudents: state.newClass.maxStudents,
        programId: state.newClass.programId,
        trainerId: trainerId,
        isActive: state.newClass.isActive,
        whatsappLink: state.newClass.whatsappLink || '',
        materials: state.newClass.materials || [],
        level: state.newClass.level || '',
        description: state.newClass.description || '',
        meetingLink: state.newClass.meetingLink || '',
        meetingLinkType: state.newClass.meetingLinkType || 'manual'
      };

      let payloads = [];

      if (state.newClass.repeat) {
        payloads = state.newClass.repeatDays.map(day => ({
          ...basePayload,
          day,
          repeat: false,
          repeatDays: [],
          repeatUntil: state.newClass.repeatUntil
        }));
      } else {
        payloads = [{
          ...basePayload,
          day: state.newClass.day,
          repeat: false,
          repeatDays: [],
          repeatUntil: ''
        }];
      }

      if (state.ui.isEditing) {
        // If repeat weekly is enabled, update all related classes
        if (state.newClass.repeat && state.newClass.repeatDays.length > 0) {
          // Get all class IDs to update (current + related)
          const allClassIds = [state.ui.isEditing, ...(state.ui.editingRelatedClasses || [])];
          
          // Filter to only update classes for days that are still selected
          const classesToUpdate = allClassIds.filter((classId, index) => {
            const classItem = state.classes.find(c => c._id === classId);
            return classItem && state.newClass.repeatDays.includes(classItem.day);
          });
          
          // Update existing classes for selected days
          await Promise.all(
            classesToUpdate.map(async (classId) => {
              const classItem = state.classes.find(c => c._id === classId);
              if (classItem) {
                const dayPayload = {
                  ...basePayload,
                  day: classItem.day,
                  repeat: false,
                  repeatDays: [],
                  repeatUntil: state.newClass.repeatUntil
                };
                await HttpInterceptor.put(`/api/classes/${classId}`, dayPayload);
              }
            })
          );
          
          // Create new classes for newly selected days
          const existingDays = classesToUpdate.map(classId => {
            const classItem = state.classes.find(c => c._id === classId);
            return classItem?.day;
          }).filter(Boolean);
          
          const newDays = state.newClass.repeatDays.filter(day => !existingDays.includes(day));
          if (newDays.length > 0) {
            await Promise.all(
              newDays.map(day => {
                const newPayload = {
                  ...basePayload,
                  day,
                  repeat: false,
                  repeatDays: [],
                  repeatUntil: state.newClass.repeatUntil
                };
                return HttpInterceptor.post('/api/classes', newPayload);
              })
            );
          }
          
          // Delete classes for unselected days
          const selectedDays = state.newClass.repeatDays;
          const classesToDelete = allClassIds.filter(classId => {
            const classItem = state.classes.find(c => c._id === classId);
            return classItem && !selectedDays.includes(classItem.day);
          });
          
          if (classesToDelete.length > 0) {
            await Promise.all(
              classesToDelete.map(classId => HttpInterceptor.delete(`/api/classes/${classId}`))
            );
          }
          
          toast.success(`Successfully updated ${classesToUpdate.length} class${classesToUpdate.length > 1 ? 'es' : ''}${newDays.length > 0 ? ` and created ${newDays.length} new class${newDays.length > 1 ? 'es' : ''}` : ''}${classesToDelete.length > 0 ? ` and deleted ${classesToDelete.length} class${classesToDelete.length > 1 ? 'es' : ''}` : ''}`);
        } else {
          // Single class update (no repeat)
        await HttpInterceptor.put(`/api/classes/${state.ui.isEditing}`, payloads[0]);
        toast.success('Class updated successfully');
        }
      } else {
        await Promise.all(
          payloads.map(payload =>
            HttpInterceptor.post('/api/classes', payload)
          )
        );
        toast.success(`Successfully created ${payloads.length} class${payloads.length > 1 ? 'es' : ''}`);
      }

      await fetchClasses(state.userPrograms, state.filters.program, trainerId);
      resetForm();
    } catch (error) {
      // Handle different error types
      let errorMessage = 'Failed to save class';
      if (error.type === 'network') {
        errorMessage = 'Network error. Please check your connection.';
      } else if (error.status === 401) {
        errorMessage = 'Session expired. Please log in again.';
      } else if (error.status === 403) {
        errorMessage = 'You don\'t have permission to create classes.';
      } else if (error.status === 409) {
        errorMessage = error.response?.data?.message || 'A class with these details already exists.';
      } else if (error.status >= 500) {
        errorMessage = 'We’re having trouble right now. Please try again in a moment.';
      } else if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      // Only show toast if it's a server error (HttpInterceptor handles network errors)
      if (error.type !== 'network' && error.status !== 401) {
        toast.error(errorMessage);
      }
      console.error('Save class error:', error);
    } finally {
      setState(prev => ({ ...prev, ui: { ...prev.ui, isLoading: false } }));
    }
  };

  const handleEdit = (classItem) => {
    // Prevent editing if program has ended or is inactive
    const programHasEnded = classItem.programHasEnded || false;
    const programIsInactive = classItem.programIsInactive || false;
    if (programHasEnded || programIsInactive) {
      const message = programHasEnded 
        ? 'Cannot edit class. The program has ended. Please update the program\'s effective date to reactivate.'
        : 'Cannot edit class. The program is inactive. Please activate the program first.';
      toast.error(message);
      return;
    }
    
    // Find related classes (same classType, programId, time, but different days)
    // These are likely from a repeat weekly creation
    const relatedClasses = state.classes.filter(c => 
      c.classType === classItem.classType &&
      (c.programId?._id || c.programId) === (classItem.programId?._id || classItem.programId) &&
      c.time === classItem.time &&
      c._id !== classItem._id
    );
    
    // If there are related classes, enable repeat weekly mode
    const hasRelatedClasses = relatedClasses.length > 0;
    const allRelatedDays = hasRelatedClasses 
      ? [classItem.day, ...relatedClasses.map(c => c.day)].sort()
      : [classItem.day];
    
    // Format repeatUntil date for HTML date input (YYYY-MM-DD format)
    let formattedRepeatUntil = '';
    if (classItem.repeatUntil) {
      try {
        // If it's already in YYYY-MM-DD format, use it directly
        if (/^\d{4}-\d{2}-\d{2}$/.test(classItem.repeatUntil)) {
          formattedRepeatUntil = classItem.repeatUntil;
        } else {
          // Try to parse and format the date
          const date = new Date(classItem.repeatUntil);
          if (!isNaN(date.getTime())) {
            formattedRepeatUntil = date.toISOString().split('T')[0];
          }
        }
      } catch (e) {
        console.warn('Error formatting repeatUntil date:', e);
        formattedRepeatUntil = '';
      }
    }
    
    // Directly open edit form modal with class data
    setState(prev => ({
      ...prev,
      newClass: {
        day: classItem.day || 'Monday',
        time: classItem.time || '',
        classType: classItem.classType || '',
        classDuration: classItem.classDuration || '1 hour',
        maxStudents: classItem.maxStudents || MAX_FREE_STUDENTS,
        repeat: hasRelatedClasses || classItem.repeat || false, // Enable repeat if there are related classes or if class has repeat flag
        repeatDays: allRelatedDays, // Pre-select all related days
        repeatUntil: formattedRepeatUntil,
        programId: classItem.programId?._id || classItem.programId || '',
        isActive: classItem.isActive !== undefined ? classItem.isActive : true,
        whatsappLink: classItem.whatsappLink || '',
        materials: Array.isArray(classItem.materials) ? classItem.materials : [],
        level: classItem.level || '',
        description: classItem.description || '',
        meetingLink: classItem.meetingLink || '',
        meetingLinkType: classItem.meetingLinkType || 'manual'
      },
      ui: {
        ...prev.ui,
        isEditing: classItem._id,
        editingClassData: classItem, // Store the full class data
        editingRelatedClasses: hasRelatedClasses ? relatedClasses.map(c => c._id) : [], // Store related class IDs
        showEditConfirm: false, // No initial confirmation
        showAddClass: true, // Show inline form for editing
        showEditFormModal: false // Don't use modal
      }
    }));
    
    // Scroll to edit form after state update
    setTimeout(() => {
      editFormRef.current?.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }, 100);
  };

  const handleDelete = (id) => {
    setState(prev => ({ ...prev, ui: { ...prev.ui, showDeleteConfirm: id } }));
  };

  const confirmDelete = async () => {
    try {
      const classId = state.ui.showDeleteConfirm;
      
      // Sanitize classId to prevent path traversal attacks
      if (!classId || typeof classId !== 'string') {
        throw new Error('Invalid class ID');
      }
      
      // Only allow alphanumeric, hyphens, and underscores (MongoDB ObjectId format)
      const sanitizedClassId = classId.replace(/[^a-zA-Z0-9\-_]/g, '');
      if (sanitizedClassId !== classId || sanitizedClassId.length === 0) {
        throw new Error('Invalid class ID format');
      }
      
      setState(prev => ({ ...prev, ui: { ...prev.ui, isLoading: true } }));
      await HttpInterceptor.delete(`/api/classes/${sanitizedClassId}`);
      toast.success('Class deleted successfully');
      await fetchClasses(state.userPrograms, state.filters.program, trainerId);
      setState(prev => ({ ...prev, ui: { ...prev.ui, showDeleteConfirm: null, isLoading: false } }));
    } catch (error) {
      // Handle different error types
      let errorMessage = 'Failed to delete class';
      if (error.type === 'network') {
        errorMessage = 'Network error. Please check your connection.';
      } else if (error.status === 401) {
        errorMessage = 'Session expired. Please log in again.';
      } else if (error.status === 403) {
        errorMessage = 'You don\'t have permission to delete this class.';
      } else if (error.status === 404) {
        errorMessage = 'Class not found. It may have already been deleted.';
      } else if (error.status >= 500) {
        errorMessage = 'We’re having trouble right now. Please try again in a moment.';
      } else if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      // Only show toast if it's a server error (HttpInterceptor handles network errors)
      if (error.type !== 'network' && error.status !== 401) {
        toast.error(errorMessage);
      }
      console.error('Delete class error:', error);
      setState(prev => ({ ...prev, ui: { ...prev.ui, isLoading: false } }));
    }
  };

  const toggleRepeat = () => {
    setState(prev => ({
      ...prev,
      newClass: {
        ...prev.newClass,
        repeat: !prev.newClass.repeat,
        repeatDays: !prev.newClass.repeat ? [prev.newClass.day] : []
      }
    }));
  };

  const toggleDaySelection = (day) => {
    setState(prev => ({
      ...prev,
      newClass: {
        ...prev.newClass,
        repeatDays: prev.newClass.repeatDays.includes(day)
          ? prev.newClass.repeatDays.filter(d => d !== day)
          : [...prev.newClass.repeatDays, day]
      }
    }));
  };

  const toggleClassStatus = async (classId, currentStatus) => {
    // Find the class to check if program has ended or is inactive
    const classItem = state.classes.find(c => c._id === classId);
    const programHasEnded = classItem?.programHasEnded || false;
    const programIsInactive = classItem?.programIsInactive || false;
    if (programHasEnded || programIsInactive) {
      const message = programHasEnded
        ? 'Cannot change class status. The program has ended. Please update the program\'s effective date to reactivate.'
        : 'Cannot change class status. The program is inactive. Please activate the program first.';
      toast.error(message);
      return;
    }
    
    // Optimistically update the UI
    const newStatus = !currentStatus;
    setState(prev => ({
      ...prev,
      classes: prev.classes.map(c => 
        c._id === classId ? { ...c, isActive: newStatus } : c
      ),
      ui: { ...prev.ui, isLoading: true }
    }));
    
    try {
      await HttpInterceptor.patch(
        `/api/classes/${classId}/status`,
        { isActive: newStatus }
      );
      toast.success(`Class ${newStatus ? 'activated' : 'deactivated'} successfully`);
      // Refresh to ensure consistency with backend
      await fetchClasses(state.userPrograms, state.filters.program, trainerId);
    } catch (error) {
      // Revert optimistic update on error
      setState(prev => ({
        ...prev,
        classes: prev.classes.map(c => 
          c._id === classId ? { ...c, isActive: currentStatus } : c
        )
      }));
      const errorMessage = error.response?.data?.message || 'Failed to update class status';
      toast.error(errorMessage);
      console.error('Update class status error:', error);
    } finally {
      setState(prev => ({ ...prev, ui: { ...prev.ui, isLoading: false } }));
    }
  };

  const resetForm = () => {
    // Clear editingRelatedClasses when resetting
    const firstActiveProgram = state.userPrograms.find(p => p.isActive)?._id || '';
    setState(prev => ({
      ...prev,
      newClass: {
        day: 'Monday',
        time: '',
        classType: '',
        classDuration: '1 hour',
        maxStudents: MAX_FREE_STUDENTS,
        repeat: false,
        repeatDays: [],
        repeatUntil: '',
        programId: firstActiveProgram,
        isActive: true,
        whatsappLink: '',
        materials: [],
        level: '',
        description: '',
        meetingLink: '',
        meetingLinkType: 'manual'
      },
      ui: {
        ...prev.ui,
        isEditing: null,
        editingClassData: null,
        editingRelatedClasses: [],
        showAddClass: false,
        showEditConfirm: false,
        showEditFormModal: false
      }
    }));
  };

  // Fetch class students with visibility
  const fetchClassStudents = async (classId) => {
    // Check if class is inactive or program is inactive/ended
    const classItem = state.classes.find(c => c._id === classId);
    if (!classItem) {
      toast.error('Class not found');
      return;
    }
    if (!classItem.isActive) {
      toast.error('Cannot view students. Class is inactive.');
      return;
    }
    const programHasEnded = classItem.programHasEnded || false;
    const programIsInactive = classItem.programIsInactive || false;
    if (programHasEnded || programIsInactive) {
      const message = programHasEnded 
        ? 'Cannot view students. Program has ended.'
        : 'Cannot view students. Program is inactive.';
      toast.error(message);
      return;
    }
    
    try {
      const response = await HttpInterceptor.get(`/api/classes/${classId}/enrollment`);
      if (response.status === 200) {
        const data = response.data;
        setState(prev => ({
          ...prev,
          ui: {
            ...prev.ui,
            showStudentsModal: classId,
            classStudents: data?.visibleStudents || [],
            classEnrollmentTotal: data?.totalEnrolled || 0,
            classEnrollmentVisible: data?.visibleCount || 0
          }
        }));
      }
    } catch (error) {
      console.error('Error fetching class enrollment:', error);
      toast.error('Failed to load students');
    }
  };

  // Fetch class attendance and enrolled students
  const fetchClassAttendance = async (classId, sessionDate = null) => {
    // Check if class is inactive or program is inactive/ended
    const classItem = state.classes.find(c => c._id === classId);
    if (!classItem) {
      toast.error('Class not found');
      return;
    }
    if (!classItem.isActive) {
      toast.error('Cannot view attendance. Class is inactive.');
      return;
    }
    const programHasEnded = classItem.programHasEnded || false;
    const programIsInactive = classItem.programIsInactive || false;
    if (programHasEnded || programIsInactive) {
      const message = programHasEnded 
        ? 'Cannot view attendance. Program has ended.'
        : 'Cannot view attendance. Program is inactive.';
      toast.error(message);
      return;
    }
    
    try {
      const date = sessionDate || new Date().toISOString().split('T')[0];
      const params = { sessionDate: date };
      
      // Fetch both attendance records and enrolled students
      const [attendanceResponse, studentsResponse] = await Promise.all([
        HttpInterceptor.get(`/api/classes/${classId}/attendance`, { params }).catch(() => ({ data: { attendance: [] } })),
        HttpInterceptor.get(`/api/classes/${classId}/students`).catch(() => ({ data: { students: [] } }))
      ]);

      const attendanceRecords = attendanceResponse.data?.attendance || [];
      const enrolledStudents = studentsResponse.data?.students || [];
      
      // Create a map of attendance records by student ID
      const attendanceMap = new Map();
      attendanceRecords.forEach(record => {
        const studentId = record.studentId?._id || record.studentId;
        attendanceMap.set(String(studentId), record);
      });

      // Combine enrolled students with their attendance records
      const studentsWithAttendance = enrolledStudents.map(student => {
        const studentId = String(student._id);
        const attendanceRecord = attendanceMap.get(studentId);
        
        return {
          studentId: student._id,
          studentName: formatName(student.personalDetails?.name || student.user?.name || 'Student'),
          studentEmail: student.personalDetails?.email || student.user?.email || '',
          attendance: attendanceRecord ? {
            _id: attendanceRecord._id,
            status: attendanceRecord.status,
            sessionDate: attendanceRecord.sessionDate,
            notes: attendanceRecord.notes
          } : null
        };
      });

        setState(prev => ({
          ...prev,
          ui: {
            ...prev.ui,
            showAttendanceModal: classId,
          classAttendance: studentsWithAttendance,
          attendanceSessionDate: date
          }
        }));
    } catch (error) {
      console.error('Error fetching class attendance:', error);
      toast.error('Failed to load attendance');
    }
  };

  // Mark attendance for a student
  const handleMarkAttendance = async (classId, studentId, status, sessionDate) => {
    try {
      // Ensure studentId is a string (convert ObjectId if needed)
      const studentIdStr = studentId?._id ? String(studentId._id) : String(studentId);
      
      if (!studentIdStr || studentIdStr === 'undefined' || studentIdStr === 'null') {
        toast.error('Invalid student ID');
        console.error('Invalid studentId:', studentId);
        return;
      }

      const date = sessionDate || state.ui.attendanceSessionDate || new Date().toISOString().split('T')[0];
      
      console.log('Marking attendance:', { classId, studentId: studentIdStr, status, sessionDate: date });
      
      const response = await HttpInterceptor.post(`/api/classes/${classId}/attendance`, {
        studentId: studentIdStr,
        status,
        sessionDate: date
      });
      
      if (response.status === 200) {
        toast.success('Attendance marked successfully');
        // Refresh attendance data
        fetchClassAttendance(classId, date);
      }
    } catch (error) {
      console.error('Error marking attendance:', error);
      const errorMessage = error.response?.data?.message || 'Failed to mark attendance';
      toast.error(errorMessage);
    }
  };

  // Fetch class recordings
  const fetchClassRecordings = async (classId) => {
    // Check if class is inactive or program is inactive/ended
    const classItem = state.classes.find(c => c._id === classId);
    if (!classItem) {
      toast.error('Class not found');
      return;
    }
    if (!classItem.isActive) {
      toast.error('Cannot view recordings. Class is inactive.');
      return;
    }
    const programHasEnded = classItem.programHasEnded || false;
    const programIsInactive = classItem.programIsInactive || false;
    if (programHasEnded || programIsInactive) {
      const message = programHasEnded 
        ? 'Cannot view recordings. Program has ended.'
        : 'Cannot view recordings. Program is inactive.';
      toast.error(message);
      return;
    }
    
    try {
      const response = await HttpInterceptor.get(`/api/classes/${classId}/recordings`);
      if (response.status === 200) {
        setState(prev => ({
          ...prev,
          ui: {
            ...prev.ui,
            showRecordingsModal: classId,
            classRecordings: response.data?.recordings || []
          }
        }));
      }
    } catch (error) {
      console.error('Error fetching recordings:', error);
      toast.error('Failed to load recordings');
    }
  };

  // Upload recording
  const handleUploadRecording = async (classId, url, title) => {
    try {
      const response = await HttpInterceptor.post(`/api/classes/${classId}/recordings`, {
        url,
        title
      });
      if (response.status === 200) {
        toast.success('Recording uploaded successfully');
        fetchClassRecordings(classId);
      }
    } catch (error) {
      console.error('Error uploading recording:', error);
      toast.error('Failed to upload recording');
    }
  };

  // Delete recording
  const handleDeleteRecording = async (classId, recordingId) => {
    try {
      const response = await HttpInterceptor.delete(`/api/classes/${classId}/recordings/${recordingId}`);
      if (response.status === 200) {
        toast.success('Recording deleted successfully');
        fetchClassRecordings(classId);
      }
    } catch (error) {
      console.error('Error deleting recording:', error);
      toast.error('Failed to delete recording');
    }
  };

  // Add material
  const handleAddMaterial = (material) => {
    if (material.trim()) {
      setState(prev => ({
        ...prev,
        newClass: {
          ...prev.newClass,
          materials: [...prev.newClass.materials, material.trim()]
        }
      }));
    }
  };

  // Remove material
  const handleRemoveMaterial = (index) => {
    setState(prev => ({
      ...prev,
      newClass: {
        ...prev.newClass,
        materials: prev.newClass.materials.filter((_, i) => i !== index)
      }
    }));
  };

  const ProModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-sm border border-gray-200">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">Upgrade to Pro</h3>
          <button 
            onClick={() => setState(prev => ({ ...prev, ui: { ...prev.ui, showProModal: false } }))} 
            className="text-gray-600 hover:text-gray-800"
            aria-label="Close modal"
          >
            <FiX size={18} />
          </button>
        </div>
        <div className="space-y-4">
          <div className="flex items-start gap-3 bg-red-50 p-4 rounded-lg">
            <FiAlertTriangle className="mt-0.5 h-5 w-5 text-red-600" />
            <p className="text-sm text-red-700">Free account limited to {MAX_FREE_STUDENTS} students per class. Upgrade to enroll up to 50 students per class.</p>
          </div>
          <ul className="space-y-2">
            {['Up to 50 students per class', 'Multiple services support'].map((benefit, index) => (
              <li key={index} className="flex items-center gap-2">
                <svg className="h-5 w-5 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-sm text-gray-600">{benefit}</span>
              </li>
            ))}
          </ul>
          <div className="flex gap-3 mt-6">
            <button
              onClick={() => setState(prev => ({ ...prev, ui: { ...prev.ui, showProModal: false } }))}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-50 transition-colors"
            >
              Continue Free
            </button>
            <button
              onClick={() => window.location.href = '/pricing'}
              className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700 transition-colors shadow-sm"
            >
              Upgrade Now
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const DeleteConfirmModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-sm border border-gray-200">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Delete Class</h3>
        <p className="text-sm text-gray-600 mb-6">Are you sure you want to delete this class? This action cannot be undone.</p>
        <div className="flex justify-end gap-3">
          <button
            onClick={() => setState(prev => ({ ...prev, ui: { ...prev.ui, showDeleteConfirm: null } }))}
            disabled={state.ui.isLoading}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 rounded-lg text-sm font-medium"
          >
            Cancel
          </button>
          <button
            onClick={confirmDelete}
            disabled={state.ui.isLoading}
            className={`px-4 py-2 bg-rose-600 text-white rounded-lg text-sm font-medium hover:bg-rose-700 transition-colors shadow-sm ${state.ui.isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {state.ui.isLoading ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );



  return (
    <div className={`min-h-screen ${isPWA ? 'bg-gradient-to-b from-orange-50/40 via-amber-50/30 to-white' : 'bg-gray-50'} py-12 px-4 sm:px-6 lg:px-8`}>
      {state.ui.showProModal && <ProModal />}
      {state.ui.showDeleteConfirm && <DeleteConfirmModal />}

      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className={`relative ${isPWA ? 'bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50' : 'bg-gradient-to-r from-orange-600 to-amber-700'} ${isPWA ? 'text-gray-900' : 'text-white'} rounded-2xl ${isPWA ? 'mb-6 sm:mb-8' : 'mb-8'} overflow-hidden`}>
          {isPWA && (
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-400 via-amber-400 to-yellow-400"></div>
          )}
          {!isPWA && (
            <div className="absolute inset-0 bg-black opacity-20"></div>
          )}
          <div className={`relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${isPWA ? 'py-8 sm:py-10 pb-12 sm:pb-14' : 'py-12'}`}>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className={`${isPWA ? 'text-xl sm:text-2xl' : 'text-4xl'} font-bold ${isPWA ? 'text-orange-800' : ''} ${isPWA ? 'mb-3' : 'mb-2'} flex items-center gap-2`}>
                  <FiUsers className={isPWA ? 'text-orange-600' : 'text-white'} />
                  Class Management
                </h1>
                <p className={`${isPWA ? 'text-sm sm:text-base text-orange-700' : 'text-white/90'}`}>Manage your classes for selected programs</p>
              </div>
              <button
                onClick={() => setState(prev => ({ ...prev, ui: { ...prev.ui, showAddClass: true } }))}
                disabled={state.ui.isLoading || activePrograms.length === 0}
                className={`flex items-center gap-2 ${isPWA ? 'bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white shadow-lg hover:shadow-xl' : 'bg-orange-600 hover:bg-orange-700 text-white'} px-4 py-2 ${isPWA ? 'px-5 py-2.5' : ''} rounded-lg transition-all duration-200 ${isPWA ? 'font-medium' : 'shadow-sm'} ${state.ui.isLoading || activePrograms.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <FiPlus size={18} />
                Add Class
              </button>
            </div>
          </div>
        </div>

        {/* Filter Bar Section */}
        <div className={`${isPWA ? 'bg-gradient-to-br from-white to-orange-50/30 border-2 border-orange-200/50 shadow-xl' : 'bg-white rounded-xl shadow-sm border border-gray-200'} ${isPWA ? 'p-4 sm:p-5' : 'p-4'} ${isPWA ? 'rounded-2xl' : ''} mb-6`}>
          <div className={`grid ${isPWA ? 'grid-cols-2 gap-2 sm:gap-3' : 'grid-cols-1 md:grid-cols-3 gap-4'}`}>
            {/* Program Filter */}
            <div>
              <label className={`block ${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} font-medium ${isPWA ? 'text-orange-800' : 'text-gray-700'} mb-1`}>Program</label>
              <select
                value={state.filters.program}
                onChange={(e) => {
                  const programId = e.target.value === 'All' ? 
                    (activePrograms[0]?._id || '') : 
                    e.target.value;
                  setState(prev => ({
                    ...prev,
                    filters: {
                      ...prev.filters,
                      program: e.target.value
                    },
                    newClass: {
                      ...prev.newClass,
                      programId
                    }
                  }));
                }}
                className={`w-full ${isPWA ? 'px-2 py-1.5 text-xs sm:text-sm border-2 border-orange-200 focus:border-orange-400 focus:ring-orange-400' : 'px-3 py-2 border border-gray-300 focus:ring-orange-500 focus:border-orange-500'} rounded-lg focus:outline-none focus:ring-2 ${isPWA ? 'bg-white/80' : 'bg-white'}`}
                disabled={state.ui.isLoading || state.userPrograms.length === 0}
              >
                {state.userPrograms.length === 0 ? (
                  <option value="">No programs</option>
                ) : (
                  <>
                    <option value="All">All Programs</option>
                    {state.userPrograms.map(program => (
                      <option key={program._id} value={program._id}>
                        {program.name} ({program.isActive ? 'Active' : 'Inactive'})
                      </option>
                    ))}
                  </>
                )}
              </select>
            </div>

            {/* Day Filter */}
            <div>
              <label className={`block ${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} font-medium ${isPWA ? 'text-orange-800' : 'text-gray-700'} mb-1`}>Day</label>
              <select
                value={state.filters.day}
                onChange={(e) => setState(prev => ({
                  ...prev,
                  filters: {
                    ...prev.filters,
                    day: e.target.value
                  }
                }))}
                className={`w-full ${isPWA ? 'px-2 py-1.5 text-xs sm:text-sm border-2 border-orange-200 focus:border-orange-400 focus:ring-orange-400' : 'px-3 py-2 border border-gray-300 focus:ring-orange-500 focus:border-orange-500'} rounded-lg focus:outline-none focus:ring-2 ${isPWA ? 'bg-white/80' : 'bg-white'}`}
                disabled={state.ui.isLoading}
              >
                <option value="All">All Days</option>
                {DAYS_OF_WEEK.map(day => (
                  <option key={day} value={day}>{day}</option>
                ))}
              </select>
            </div>

            {/* Reset Button - Only show when filters are active */}
            {(state.filters.program !== 'All' || state.filters.day !== 'All') && (
              <div className={`${isPWA ? 'col-span-2' : 'md:col-span-1'} flex items-end`}>
                <button
                  onClick={() => setState(prev => ({
                    ...prev,
                    filters: {
                      day: 'All',
                      program: 'All'
                    }
                  }))}
                  className={`w-full ${isPWA ? 'px-3 py-2 text-xs sm:text-sm bg-gradient-to-r from-orange-100 to-amber-100 hover:from-orange-200 hover:to-amber-200 text-orange-700 border-2 border-orange-200' : 'px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700'} rounded-lg transition-all duration-200 ${isPWA ? 'font-medium shadow-sm' : ''}`}
                  aria-label="Reset all filters"
                >
                  Reset Filters
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Add/Edit Class Form (inline) */}
        {state.ui.showAddClass && (
          <div ref={editFormRef} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-medium text-gray-900">
                {state.ui.isEditing ? 'Edit Class' : 'Add New Class'}
              </h2>
              <button 
                onClick={resetForm} 
                className="text-gray-600 hover:text-gray-800"
                aria-label="Close form"
              >
                <FiX size={18} />
              </button>
            </div>

            {/* Confirmation overlay for editing */}
            {state.ui.showEditConfirm && state.ui.isEditing && (
              <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <h3 className="text-sm font-medium text-yellow-900 mb-2">Confirm Update</h3>
                <p className="text-sm text-yellow-700 mb-4">Are you sure you want to update this class?</p>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setState(prev => ({ ...prev, ui: { ...prev.ui, showEditConfirm: false } }))}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 rounded-lg text-sm font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      setState(prev => ({ ...prev, ui: { ...prev.ui, showEditConfirm: false } }));
                      await handleAddOrUpdateClass();
                    }}
                    className="px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700 transition-colors shadow-sm"
                  >
                    Confirm Update
                  </button>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="md:col-span-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={state.newClass.repeat}
                    onChange={toggleRepeat}
                    className="rounded text-orange-600 focus:ring-orange-500"
                    disabled={activePrograms.length === 0}
                  />
                  <span className="text-sm font-medium text-gray-700">Repeat weekly</span>
                </label>
              </div>

              {!state.newClass.repeat ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Day*</label>
                  <select
                    name="day"
                    value={state.newClass.day}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                    required
                    disabled={activePrograms.length === 0}
                  >
                    {DAYS_OF_WEEK.map(day => (
                      <option key={day} value={day}>{day}</option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Repeat on*</label>
                  <div className="flex flex-wrap gap-2">
                    {DAYS_OF_WEEK.map(day => (
                      <button
                        key={day}
                        type="button"
                        onClick={() => toggleDaySelection(day)}
                        className={`px-4 py-2 rounded-full text-sm font-medium ${state.newClass.repeatDays.includes(day) ? 'bg-orange-600 text-white' : 'bg-gray-100 text-gray-800'} ${activePrograms.length === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-orange-500 hover:text-white transition-colors'}`}
                        disabled={activePrograms.length === 0}
                        aria-label={`Select ${day}`}
                      >
                        {day.substring(0, 3)}
                      </button>
                    ))}
                  </div>
                  {state.newClass.repeat && state.newClass.repeatDays.length === 0 && (
                    <p className="mt-2 text-sm text-red-500">Please select at least one day</p>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Program*</label>
                <select
                  name="programId"
                  value={state.newClass.programId}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  required
                  disabled={activePrograms.length === 0}
                >
                  {activePrograms.length === 0 ? (
                    <option value="">No active programs available</option>
                  ) : (
                    activePrograms.map(program => (
                      <option key={program._id} value={program._id}>
                        {program.name}
                      </option>
                    ))
                  )}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Class Type*</label>
                <input
                  type="text"
                  value={state.newClass.classType}
                  onChange={handleClassTypeChange}
                  placeholder="e.g. Hatha Yoga, Vinyasa Flow"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  required
                  disabled={activePrograms.length === 0}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Time*</label>
                 <TimePicker
                  value={state.newClass.time}
                   onChange={handleTimeChange}
                   timeFormat={state.dateTimeSettings.timeFormat}
                   placeholder="Select class time"
                  disabled={activePrograms.length === 0}
                   timeIntervals={15}
                   dateFormat={state.dateTimeSettings.timeFormat === '12-hour' ? "h:mm aa" : "HH:mm"}
                />
                <p className="mt-1 text-xs text-gray-500">
                  {state.dateTimeSettings.timeFormat === '12-hour' 
                    ? 'Time will be displayed in 12-hour format (e.g., 2:30 PM)' 
                    : 'Time will be displayed in 24-hour format (e.g., 14:30)'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Class Duration</label>
                <select
                  name="classDuration"
                  value={state.newClass.classDuration}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  disabled={activePrograms.length === 0}
                >
                  {CLASS_DURATION_OPTIONS.map(duration => (
                    <option key={duration} value={duration}>{duration}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Students</label>
                <input
                  type="number"
                  name="maxStudents"
                  min="1"
                  max="50"
                  value={state.newClass.maxStudents}
                  onChange={handleMaxStudentsChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  disabled={activePrograms.length === 0}
                />
                {state.newClass.maxStudents > MAX_FREE_STUDENTS && (
                  <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                    <FiAlertTriangle size={14} />
                    Free version limited to {MAX_FREE_STUDENTS} students
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Class Status</label>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={state.newClass.isActive}
                    onChange={() => setState(prev => ({
                      ...prev,
                      newClass: {
                        ...prev.newClass,
                        isActive: !prev.newClass.isActive
                      }
                    }))}
                    className="sr-only peer"
                    disabled={activePrograms.length === 0}
                  />
                  <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer ${state.newClass.isActive ? 'peer-checked:bg-orange-600' : ''} peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all`}>
                    {state.newClass.isActive ? (
                      <FiToggleRight className="absolute top-0.5 left-0.5 text-orange-600 w-5 h-5" />
                    ) : (
                      <FiToggleLeft className="absolute top-0.5 right-0.5 text-gray-400 w-5 h-5" />
                    )}
                  </div>
                  <span className="ml-3 text-sm font-medium text-gray-700">
                    {state.newClass.isActive ? 'Active' : 'Inactive'}
                  </span>
                </label>
              </div>

              {state.newClass.repeat && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Repeat until</label>
                  <input
                    type="date"
                    name="repeatUntil"
                    value={state.newClass.repeatUntil}
                    onChange={handleInputChange}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                    disabled={activePrograms.length === 0}
                  />
                </div>
              )}

              {/* Class Level */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Class Level</label>
                <select
                  name="level"
                  value={state.newClass.level}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  disabled={activePrograms.length === 0}
                >
                  <option value="">Select Level</option>
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                </select>
              </div>

              {/* Google Meet Link */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Google Meet Link <span className="text-gray-500 text-xs">(Optional)</span>
                </label>
                <input
                  type="url"
                  name="meetingLink"
                  value={state.newClass.meetingLink}
                  onChange={handleInputChange}
                  placeholder="https://meet.google.com/xxx-xxxx-xxx"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  disabled={activePrograms.length === 0}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Only paid students will be able to access this link
                </p>
              </div>

              {/* WhatsApp Link */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  WhatsApp Group Link <span className="text-gray-500 text-xs">(Optional)</span>
                </label>
                <input
                  type="url"
                  name="whatsappLink"
                  value={state.newClass.whatsappLink}
                  onChange={handleInputChange}
                  placeholder="https://chat.whatsapp.com/..."
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  disabled={activePrograms.length === 0}
                />
              </div>

              {/* Class Description */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Class Description</label>
                <textarea
                  name="description"
                  value={state.newClass.description}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Describe what students will learn in this class..."
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  disabled={activePrograms.length === 0}
                />
              </div>

              {/* Class Materials */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Required Materials</label>
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="e.g. Yoga mat, Water bottle"
                      className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                      disabled={activePrograms.length === 0}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddMaterial(e.target.value);
                          e.target.value = '';
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        const input = e.target.previousElementSibling;
                        if (input && input.value.trim()) {
                          handleAddMaterial(input.value);
                          input.value = '';
                        }
                      }}
                      className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={activePrograms.length === 0}
                    >
                      Add
                    </button>
                  </div>
                  {state.newClass.materials.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {state.newClass.materials.map((material, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                        >
                          {material}
                          <button
                            type="button"
                            onClick={() => handleRemoveMaterial(index)}
                            className="text-gray-500 hover:text-gray-700"
                          >
                            <FiX size={14} />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={state.ui.isEditing ? () => setState(prev => ({ ...prev, ui: { ...prev.ui, showEditConfirm: true } })) : handleAddOrUpdateClass}
                disabled={state.ui.isLoading || activePrograms.length === 0}
                className={`flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 transition-colors shadow-sm ${state.ui.isLoading || activePrograms.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {state.ui.isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {state.ui.isEditing ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  <>
                    {state.ui.isEditing ? (
                      <>
                        <FiCheck size={18} />
                        Update Class
                      </>
                    ) : (
                      <>
                        Add Class
                      </>
                    )}
                  </>
                )}
              </button>
              <button
                onClick={resetForm}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 rounded-lg font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Class List */}
        <div className={`${isPWA ? 'bg-gradient-to-br from-white to-orange-50/30 border-2 border-orange-200/50 shadow-xl' : 'bg-white rounded-xl shadow-sm border border-gray-200'} ${isPWA ? 'rounded-2xl' : ''} overflow-hidden relative`}>
          {isPWA && (
            <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none z-0">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-400 via-amber-400 to-yellow-400"></div>
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-orange-100/20 to-amber-100/10 rounded-full blur-2xl"></div>
            </div>
          )}
          <div className={`${isPWA ? 'p-4 sm:p-6' : 'p-6'} ${isPWA ? 'border-b-2 border-orange-200/50' : 'border-b border-gray-200'} relative z-10`}>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div className="flex items-center gap-3">
                {isPWA && (
                  <div className="bg-gradient-to-br from-orange-500 to-amber-600 p-2 rounded-lg shadow-md">
                    <FiCalendar className="text-white text-base" />
                  </div>
                )}
                <h2 className={`${isPWA ? 'text-lg sm:text-xl font-bold text-orange-800' : 'text-lg font-medium text-gray-900'}`}>
                  {state.filters.program === 'All'
                    ? 'All Classes'
                    : `Classes for ${state.userPrograms.find(p => p._id === state.filters.program)?.name || ''}`}
                </h2>
              </div>
              <div className={`flex ${isPWA ? 'gap-2' : 'space-x-2'}`}>
                <button
                  onClick={() => setViewMode('list')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    viewMode === 'list' 
                      ? (isPWA ? 'bg-gradient-to-r from-orange-500 to-amber-600 text-white shadow-lg ring-2 ring-orange-300/50 font-semibold' : 'bg-orange-600 text-white')
                      : (isPWA ? 'bg-white/95 backdrop-blur-sm border-2 border-orange-200/50 text-orange-700 hover:bg-orange-50 shadow-md font-semibold' : 'bg-gray-200 text-gray-700 hover:bg-gray-300')
                  }`}
                >
                  List View
                </button>
                <button
                  onClick={() => setViewMode('calendar')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    viewMode === 'calendar' 
                      ? (isPWA ? 'bg-gradient-to-r from-orange-500 to-amber-600 text-white shadow-lg ring-2 ring-orange-300/50 font-semibold' : 'bg-orange-600 text-white')
                      : (isPWA ? 'bg-white/95 backdrop-blur-sm border-2 border-orange-200/50 text-orange-700 hover:bg-orange-50 shadow-md font-semibold' : 'bg-gray-200 text-gray-700 hover:bg-gray-300')
                  }`}
                >
                  Calendar View
                </button>
              </div>
            </div>
          </div>
          
          {state.ui.isLoading && !state.classes.length ? (
            <div className={`text-center py-12 relative z-10`}>
              <div className={`animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 ${isPWA ? 'border-orange-500' : 'border-orange-500'} mx-auto`}></div>
              <p className={`mt-4 ${isPWA ? 'text-orange-700 font-medium' : 'text-gray-600'}`}>Loading classes...</p>
            </div>
            ) : selectedDate ? (
              <div className={`${isPWA ? 'p-4 sm:p-6' : 'p-4 sm:p-6'} relative z-10`}>
                <DateDetailView
                  day={selectedDate.day}
                  month={selectedDate.month}
                  year={selectedDate.year}
                  classes={getClassesForDate(selectedDate.day, selectedDate.month, selectedDate.year)}
                  onBack={() => setSelectedDate(null)}
                />
              </div>
            ) : viewMode === 'calendar' ? (
              <div className={`${isPWA ? 'p-4 sm:p-6' : 'p-4 sm:p-6'} relative z-10`}>
                <CalendarView />
              </div>
            ) : (
            <div className={`${isPWA ? 'p-4 sm:p-6' : 'p-4 sm:p-6'} relative z-10`}>
              {/* Sorting Controls */}
              <div className={`${isPWA ? 'mb-5' : 'mb-4'} flex flex-wrap items-center gap-2 ${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} ${isPWA ? 'text-orange-700 font-bold' : 'text-gray-600'}`}>
                <span className="font-medium">Sort by:</span>
                <button
                  onClick={() => handleSort('classType')}
                  className={`px-3 py-1.5 rounded-lg transition-all duration-200 ${
                    state.sorting.field === 'classType'
                      ? isPWA ? 'bg-gradient-to-r from-orange-500 to-amber-600 text-white font-bold shadow-md' : 'bg-orange-100 text-orange-700 font-medium'
                      : isPWA ? 'bg-orange-50/50 text-orange-700 hover:bg-orange-100/80 border border-orange-200/50' : 'hover:bg-gray-100'
                  }`}
                >
                  Class Type
                  {state.sorting.field === 'classType' && (
                    state.sorting.direction === 'asc' ? 
                      <FiChevronUp className="inline ml-1" size={14} /> : 
                      <FiChevronDown className="inline ml-1" size={14} />
                  )}
                </button>
                <button
                  onClick={() => handleSort('program')}
                  className={`px-3 py-1.5 rounded-lg transition-all duration-200 ${
                    state.sorting.field === 'program'
                      ? isPWA ? 'bg-gradient-to-r from-orange-500 to-amber-600 text-white font-bold shadow-md' : 'bg-orange-100 text-orange-700 font-medium'
                      : isPWA ? 'bg-orange-50/50 text-orange-700 hover:bg-orange-100/80 border border-orange-200/50' : 'hover:bg-gray-100'
                  }`}
                >
                  Program
                  {state.sorting.field === 'program' && (
                    state.sorting.direction === 'asc' ? 
                      <FiChevronUp className="inline ml-1" size={14} /> : 
                      <FiChevronDown className="inline ml-1" size={14} />
                  )}
                </button>
                <button
                  onClick={() => handleSort('day')}
                  className={`px-3 py-1.5 rounded-lg transition-all duration-200 ${
                    state.sorting.field === 'day'
                      ? isPWA ? 'bg-gradient-to-r from-orange-500 to-amber-600 text-white font-bold shadow-md' : 'bg-orange-100 text-orange-700 font-medium'
                      : isPWA ? 'bg-orange-50/50 text-orange-700 hover:bg-orange-100/80 border border-orange-200/50' : 'hover:bg-gray-100'
                  }`}
                >
                  Day
                  {state.sorting.field === 'day' && (
                    state.sorting.direction === 'asc' ? 
                      <FiChevronUp className="inline ml-1" size={14} /> : 
                      <FiChevronDown className="inline ml-1" size={14} />
                  )}
                </button>
                <button
                  onClick={() => handleSort('time')}
                  className={`px-3 py-1.5 rounded-lg transition-all duration-200 ${
                    state.sorting.field === 'time'
                      ? isPWA ? 'bg-gradient-to-r from-orange-500 to-amber-600 text-white font-bold shadow-md' : 'bg-orange-100 text-orange-700 font-medium'
                      : isPWA ? 'bg-orange-50/50 text-orange-700 hover:bg-orange-100/80 border border-orange-200/50' : 'hover:bg-gray-100'
                  }`}
                >
                  Time
                  {state.sorting.field === 'time' && (
                    state.sorting.direction === 'asc' ? 
                      <FiChevronUp className="inline ml-1" size={14} /> : 
                      <FiChevronDown className="inline ml-1" size={14} />
                  )}
                </button>
              </div>

              {/* Card Grid View */}
              <div className={`grid ${isPWA ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'} ${isPWA ? 'gap-3 sm:gap-4' : 'gap-4'}`}>
                {filteredClasses.length > 0 ? (
                  filteredClasses.map((classItem) => {
                    const program = state.userPrograms.find(p => p._id === (classItem.programId?._id || classItem.programId));
                    const hasRelatedClasses = state.classes.some(c => 
                      c._id !== classItem._id &&
                      c.classType === classItem.classType &&
                      (c.programId?._id || c.programId) === (classItem.programId?._id || classItem.programId) &&
                      c.time === classItem.time
                    );
                    const programHasEnded = classItem.programHasEnded || false;
                    const programIsInactive = classItem.programIsInactive || false;
                    const shouldDisableClass = programHasEnded || programIsInactive;
                    // Use enrolledCount from backend if available, otherwise fallback to enrolledStudents array length
                    const enrolledCount = classItem.enrolledCount !== undefined 
                      ? classItem.enrolledCount 
                      : (classItem.enrolledStudents?.length || 0);
                    
                    // Calculate max allowed based on subscription plan
                    const isAdvancePlan = state.trainerSubscriptionPlan === 'advance';
                    const hasIncreasedCapacity = isAdvancePlan && state.advanceFeatures?.increasedClassCapacity;
                    const maxAllowed = hasIncreasedCapacity ? 50 : MAX_FREE_STUDENTS;
                    
                    // Use the plan limit for display (not the class's maxStudents which might be lower)
                    const displayMax = maxAllowed;
                    const enrollmentPercentage = (enrolledCount / displayMax) * 100;

                    return (
                      <div 
                        key={classItem._id} 
                        className={`${isPWA ? 'bg-gradient-to-br from-white to-orange-50/20 border-2 border-orange-200/50 shadow-xl hover:shadow-2xl hover:border-orange-300/80' : 'bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md'} ${isPWA ? 'rounded-2xl' : 'rounded-lg'} ${isPWA ? 'p-4 sm:p-5' : 'p-5'} transition-all duration-300 flex flex-col relative overflow-hidden group ${
                          hasRelatedClasses ? isPWA ? 'ring-2 ring-orange-300/50' : 'ring-2 ring-blue-200' : ''
                        } ${shouldDisableClass ? 'opacity-60' : ''}`}
                      >
                        {isPWA && (
                          <>
                            <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-orange-300/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-orange-200/30 to-amber-200/20 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-0"></div>
                          </>
                        )}
                        {/* Header Section */}
                        <div className="flex justify-between items-start mb-4 relative z-10">
                          <div className="flex-1 pr-3">
                            <h3 className={`${isPWA ? 'text-base sm:text-lg font-bold' : 'text-lg font-semibold'} ${isPWA ? 'text-orange-800' : 'text-gray-900'} mb-1 flex items-center gap-2`}>
                              <span 
                                title="Class type"
                                className="cursor-help"
                              >
                                {classItem.classType}
                              </span>
                              {hasRelatedClasses && (
                                <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${isPWA ? 'bg-gradient-to-r from-orange-400 to-amber-500 text-white shadow-sm' : 'bg-orange-100 text-orange-700'}`} title="Part of repeat weekly schedule">
                                  Repeats
                                </span>
                              )}
                            </h3>
                            <div className="flex items-center gap-2 mt-1">
                              <span 
                                className={`${isPWA ? 'text-xs sm:text-sm font-bold' : 'text-sm font-medium'} ${program?.isActive ? isPWA ? 'text-orange-700' : 'text-orange-600' : 'text-red-600'} cursor-help`}
                                title="Training program this class belongs to"
                              >
                                {program?.name || 'Unknown Program'}
                              </span>
                              <span 
                                className={`text-xs px-2 py-0.5 rounded-full font-bold cursor-help ${
                                  program?.isActive 
                                    ? isPWA ? 'bg-gradient-to-r from-green-400 to-emerald-500 text-white shadow-sm' : 'bg-orange-100 text-orange-700'
                                    : isPWA ? 'bg-gradient-to-r from-red-400 to-rose-500 text-white shadow-sm' : 'bg-red-100 text-red-700'
                                }`}
                                title={program?.isActive ? 'Program is active' : 'Program is inactive'}
                              >
                                {program?.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Status Toggle Section */}
                        <div className={`mb-4 pb-4 ${isPWA ? 'border-b-2 border-orange-200/50' : 'border-b border-gray-100'} relative z-10`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className={`${isPWA ? 'text-xs sm:text-sm font-bold' : 'text-sm font-medium'} ${isPWA ? 'text-orange-800' : 'text-gray-700'}`}>Class Status:</span>
                              <span 
                                className={`text-xs px-2.5 py-1 rounded-full font-bold cursor-help ${
                                  classItem.isActive 
                                    ? isPWA ? 'bg-gradient-to-r from-green-400 to-emerald-500 text-white shadow-sm' : 'bg-orange-100 text-orange-800'
                                    : isPWA ? 'bg-gradient-to-r from-red-400 to-rose-500 text-white shadow-sm' : 'bg-red-100 text-red-800'
                                }`}
                                title={classItem.isActive ? 'Class is active and visible to students' : 'Class is inactive and hidden from students'}
                              >
                                {classItem.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </div>
                            
                            {/* Modern Toggle Switch */}
                            <div className="flex items-center gap-3">
                              <span className={`text-xs font-medium transition-colors ${
                                classItem.isActive ? 'text-gray-500' : 'text-gray-700'
                              }`}>
                                Off
                              </span>
                              <button
                                onClick={() => !state.ui.isLoading && toggleClassStatus(classItem._id, classItem.isActive)}
                                disabled={state.ui.isLoading || shouldDisableClass}
                                title={shouldDisableClass ? (programHasEnded ? 'Program has ended. Cannot change status.' : 'Program is inactive. Cannot change status.') : (classItem.isActive ? 'Click to deactivate' : 'Click to activate')}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 ${
                                  classItem.isActive 
                                    ? 'bg-orange-600' 
                                    : shouldDisableClass
                                      ? 'bg-gray-300 cursor-not-allowed'
                                      : 'bg-gray-300'
                                } ${state.ui.isLoading || shouldDisableClass ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:shadow-md'}`}
                              >
                                <span
                                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ease-in-out ${
                                    classItem.isActive ? 'translate-x-6' : 'translate-x-1'
                                  } ${shouldDisableClass ? 'opacity-60' : ''}`}
                                >
                                  {classItem.isActive ? (
                                    <FiCheckIcon className="h-3 w-3 text-orange-600 m-0.5" />
                                  ) : shouldDisableClass ? (
                                    <FiXIcon className="h-3 w-3 text-gray-400 m-0.5" />
                                  ) : (
                                    <FiXIcon className="h-3 w-3 text-gray-400 m-0.5" />
                                  )}
                                </span>
                              </button>
                              <span className={`text-xs font-medium transition-colors ${
                                !classItem.isActive ? 'text-gray-500' : 'text-gray-700'
                              }`}>
                                On
                              </span>
                            </div>
                          </div>
                          {!shouldDisableClass && (
                            <p className={`text-xs mt-2 ml-1 ${isPWA ? 'text-orange-600/70 font-medium' : 'text-gray-500'}`}>
                              {classItem.isActive ? 'Click toggle to deactivate this class' : 'Click toggle to activate this class'}
                            </p>
                          )}
                          {shouldDisableClass && (
                            <p className={`text-xs mt-2 ml-1 flex items-center gap-1 ${isPWA ? 'text-amber-700 font-bold' : 'text-amber-600'}`}>
                              <FiAlertTriangle size={12} />
                              {programHasEnded ? 'Program has ended. Cannot change status.' : 'Program is inactive. Cannot change status.'}
                            </p>
                          )}
                        </div>

                        {/* Info Section */}
                        <div className={`${isPWA ? 'space-y-2.5 sm:space-y-3' : 'space-y-2'} mb-4 flex-grow relative z-10`}>
                          <div className={`flex items-center ${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} ${isPWA ? 'bg-orange-50/40 border border-orange-200/50 rounded-lg px-3 py-2' : ''}`}>
                            <FiCalendar className={`mr-2 flex-shrink-0 ${isPWA ? 'text-orange-500' : 'text-gray-400'}`} size={isPWA ? 14 : 16} />
                            <span className={`${isPWA ? 'font-bold' : 'font-medium'} mr-1 ${isPWA ? 'text-orange-800' : ''}`}>Day:</span>
                            <span 
                              className={`cursor-help ${isPWA ? 'text-orange-700 font-semibold' : ''}`}
                              title="Weekly scheduled day"
                            >
                              {classItem.day}
                            </span>
                          </div>
                          <div className={`flex items-center ${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} ${isPWA ? 'bg-orange-50/40 border border-orange-200/50 rounded-lg px-3 py-2' : ''}`}>
                            <FiClock className={`mr-2 flex-shrink-0 ${isPWA ? 'text-orange-500' : 'text-gray-400'}`} size={isPWA ? 14 : 16} />
                            <span className={`${isPWA ? 'font-bold' : 'font-medium'} mr-1 ${isPWA ? 'text-orange-800' : ''}`}>Time:</span>
                            <span 
                              className={`cursor-help ${isPWA ? 'text-orange-700 font-semibold' : ''}`}
                              title="Class start time"
                            >
                              {formatTime(classItem.time, state.dateTimeSettings.timeFormat)}
                            </span>
                          </div>
                          <div className={`flex items-center ${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} ${isPWA ? 'bg-orange-50/40 border border-orange-200/50 rounded-lg px-3 py-2' : ''}`}>
                            <FiClock className={`mr-2 flex-shrink-0 ${isPWA ? 'text-orange-500' : 'text-gray-400'}`} size={isPWA ? 14 : 16} />
                            <span className={`${isPWA ? 'font-bold' : 'font-medium'} mr-1 ${isPWA ? 'text-orange-800' : ''}`}>Duration:</span>
                            <span 
                              className={`cursor-help ${isPWA ? 'text-orange-700 font-semibold' : ''}`}
                              title="Class session length"
                            >
                              {classItem.classDuration}
                            </span>
                          </div>
                            <div className={`flex items-center justify-between ${isPWA ? 'bg-orange-50/40 border border-orange-200/50 rounded-lg px-3 py-2' : ''}`}>
                              <div className={`flex items-center ${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} ${isPWA ? 'text-orange-700' : 'text-gray-600'}`}>
                                <FiUsers className={`mr-2 flex-shrink-0 ${isPWA ? 'text-orange-500' : 'text-gray-400'}`} size={isPWA ? 14 : 16} />
                                <span className={`${isPWA ? 'font-bold' : 'font-medium'} mr-1 ${isPWA ? 'text-orange-800' : ''}`}>Enrolled:</span>
                                <span 
                                  className={`${enrolledCount >= displayMax ? isPWA ? 'text-red-600 font-bold' : 'text-red-600 font-semibold' : isPWA ? 'font-semibold' : ''} cursor-help`}
                                  title={`Enrolled: ${enrolledCount} / ${displayMax} (${state.trainerSubscriptionPlan === 'advance' && state.advanceFeatures?.increasedClassCapacity ? 'Advance: 50 max' : 'Free: 10 max'})`}
                                >
                                  {enrolledCount} / {displayMax}
                                </span>
                              </div>
                              <div 
                                className={`${isPWA ? 'w-20 sm:w-24' : 'w-24'} ${isPWA ? 'bg-orange-200/50' : 'bg-gray-200'} rounded-full h-2 cursor-help`}
                                title={`${Math.round(enrollmentPercentage)}% full - ${enrolledCount}/${displayMax} students`}
                              >
                                <div 
                                  className={`h-2 rounded-full transition-all ${
                                    enrollmentPercentage >= 100 ? isPWA ? 'bg-gradient-to-r from-red-500 to-rose-600' : 'bg-red-500' :
                                    enrollmentPercentage >= 80 ? isPWA ? 'bg-gradient-to-r from-yellow-500 to-amber-600' : 'bg-yellow-500' :
                                    isPWA ? 'bg-gradient-to-r from-green-500 to-emerald-600' : 'bg-green-500'
                                  }`}
                                  style={{ width: `${Math.min(enrollmentPercentage, 100)}%` }}
                                ></div>
                              </div>
                            </div>
                          {shouldDisableClass && (
                            <div className="mt-2">
                              <span className={`text-xs px-2 py-1 rounded-full font-bold ${isPWA ? 'bg-gradient-to-r from-amber-400 to-yellow-500 text-white shadow-sm' : 'bg-gray-200 text-gray-600'}`} title={programHasEnded ? 'Program has ended' : 'Program is inactive'}>
                                {programHasEnded ? 'Program Ended' : 'Program Inactive'}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Actions Section */}
                        <div className={`flex flex-wrap gap-2 pt-4 ${isPWA ? 'border-t-2 border-orange-200/50' : 'border-t border-gray-100'} mt-auto relative z-10`}>
                          <button
                            onClick={() => fetchClassStudents(classItem._id)}
                            disabled={state.ui.isLoading || !classItem.isActive || shouldDisableClass}
                            className={`flex-1 flex items-center justify-center gap-1.5 ${isPWA ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:from-blue-600 hover:to-indigo-700 shadow-md hover:shadow-lg' : 'text-blue-600 hover:text-blue-900 hover:bg-blue-50'} px-3 py-2 ${isPWA ? 'rounded-lg font-bold text-xs sm:text-sm' : 'rounded-md'} transition-all duration-200 ${isPWA ? '' : 'text-sm font-medium'} ${
                              state.ui.isLoading || !classItem.isActive || shouldDisableClass ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                            title={!classItem.isActive ? 'Class is inactive' : shouldDisableClass ? (programHasEnded ? 'Program has ended' : 'Program is inactive') : 'View Students'}
                          >
                            <FiUsers size={isPWA ? 14 : 16} />
                            <span>Students</span>
                          </button>
                          <button
                            onClick={() => fetchClassAttendance(classItem._id)}
                            disabled={state.ui.isLoading || !classItem.isActive || shouldDisableClass}
                            className={`flex-1 flex items-center justify-center gap-1.5 ${isPWA ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700 shadow-md hover:shadow-lg' : 'text-green-600 hover:text-green-900 hover:bg-green-50'} px-3 py-2 ${isPWA ? 'rounded-lg font-bold text-xs sm:text-sm' : 'rounded-md'} transition-all duration-200 ${isPWA ? '' : 'text-sm font-medium'} ${
                              state.ui.isLoading || !classItem.isActive || shouldDisableClass ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                            title={!classItem.isActive ? 'Class is inactive' : shouldDisableClass ? (programHasEnded ? 'Program has ended' : 'Program is inactive') : 'View Attendance'}
                          >
                            <FiCheck size={isPWA ? 14 : 16} />
                            <span>Attendance</span>
                          </button>
                          <button
                            onClick={() => fetchClassRecordings(classItem._id)}
                            disabled={state.ui.isLoading || !classItem.isActive || shouldDisableClass}
                            className={`flex-1 flex items-center justify-center gap-1.5 ${isPWA ? 'bg-gradient-to-r from-purple-500 to-pink-600 text-white hover:from-purple-600 hover:to-pink-700 shadow-md hover:shadow-lg' : 'text-purple-600 hover:text-purple-900 hover:bg-purple-50'} px-3 py-2 ${isPWA ? 'rounded-lg font-bold text-xs sm:text-sm' : 'rounded-md'} transition-all duration-200 ${isPWA ? '' : 'text-sm font-medium'} ${
                              state.ui.isLoading || !classItem.isActive || shouldDisableClass ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                            title={!classItem.isActive ? 'Class is inactive' : shouldDisableClass ? (programHasEnded ? 'Program has ended' : 'Program is inactive') : 'View Recordings'}
                          >
                            <FiVideo size={isPWA ? 14 : 16} />
                            <span>Recordings</span>
                          </button>
                          <button
                            onClick={() => handleEdit(classItem)}
                            disabled={state.ui.isLoading || shouldDisableClass}
                            className={`flex-1 flex items-center justify-center gap-1.5 ${isPWA ? 'bg-gradient-to-r from-orange-500 to-amber-600 text-white hover:from-orange-600 hover:to-amber-700 shadow-md hover:shadow-lg' : 'text-orange-600 hover:text-orange-900 hover:bg-orange-50'} px-3 py-2 ${isPWA ? 'rounded-lg font-bold text-xs sm:text-sm' : 'rounded-md'} transition-all duration-200 ${isPWA ? '' : 'text-sm font-medium'} ${
                              state.ui.isLoading || shouldDisableClass ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                            title={shouldDisableClass ? (programHasEnded ? 'Cannot edit class. Program has ended.' : 'Cannot edit class. Program is inactive.') : 'Edit Class'}
                          >
                            <FiEdit2 size={isPWA ? 14 : 16} />
                            <span>Edit</span>
                          </button>
                          <button
                            onClick={() => handleDelete(classItem._id)}
                            disabled={state.ui.isLoading}
                            className={`flex-1 flex items-center justify-center gap-1.5 ${isPWA ? 'bg-gradient-to-r from-red-500 to-rose-600 text-white hover:from-red-600 hover:to-rose-700 shadow-md hover:shadow-lg' : 'text-rose-600 hover:text-rose-900 hover:bg-rose-50'} px-3 py-2 ${isPWA ? 'rounded-lg font-bold text-xs sm:text-sm' : 'rounded-md'} transition-all duration-200 ${isPWA ? '' : 'text-sm font-medium'} ${
                              state.ui.isLoading ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                            title="Delete Class"
                          >
                            <FiTrash2 size={isPWA ? 14 : 16} />
                            <span>Delete</span>
                          </button>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className={`col-span-full text-center py-12 relative z-10`}>
                    <div className={`mx-auto h-24 w-24 ${isPWA ? 'text-orange-300' : 'text-gray-400'}`}>
                      <FiCalendar size={96} className="opacity-20" />
                    </div>
                    <h3 className={`mt-2 ${isPWA ? 'text-lg sm:text-xl font-bold text-orange-800' : 'text-lg font-medium text-gray-900'}`}>
                      {state.filters.program !== 'All' || state.filters.day !== 'All'
                        ? 'No classes match your filters'
                        : 'No classes created yet'}
                    </h3>
                    <p className={`mt-1 ${isPWA ? 'text-xs sm:text-sm text-orange-600/70 font-medium' : 'text-sm text-gray-500'}`}>
                      {state.filters.program !== 'All' || state.filters.day !== 'All'
                        ? 'Try adjusting your filters'
                        : 'Get started by adding your first class'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Students Modal */}
        {state.ui.showStudentsModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-lg max-w-4xl w-full max-h-[80vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">Class Students</h3>
                  {state.ui.classEnrollmentTotal > 0 && (
                    <p className="text-sm text-gray-600 mt-1">
                      {state.ui.classEnrollmentTotal} {state.ui.classEnrollmentTotal === 1 ? 'member' : 'members'} enrolled
                      {state.ui.classEnrollmentVisible < state.ui.classEnrollmentTotal && (
                        <span className="text-gray-500"> ({state.ui.classEnrollmentVisible} visible)</span>
                      )}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => setState(prev => ({ ...prev, ui: { ...prev.ui, showStudentsModal: null, classStudents: [], classEnrollmentTotal: 0, classEnrollmentVisible: 0 } }))}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <FiX size={24} />
                </button>
              </div>
              <div className="p-6">
                {state.ui.classStudents.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">
                    {state.ui.classEnrollmentTotal > 0 
                      ? 'No visible students enrolled in this class (some students have private profiles)'
                      : 'No students enrolled in this class'}
                  </p>
                ) : (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {state.ui.classStudents.map((student) => {
                        // Only show visible students
                        if (!student.isVisible) {
                          return null;
                        }
                        return (
                          <div key={student._id} className="border border-gray-200 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="font-semibold text-gray-900">
                                {student.name || 'Student'}
                              </h4>
                            </div>
                            {student.email && (
                              <p className="text-sm text-gray-600">{student.email}</p>
                            )}
                            {student.attendance && (
                              <p className="text-sm text-gray-600 mt-1">
                                Progress: {student.attendance?.attendancePercentage || 0}%
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Attendance Modal */}
        {state.ui.showAttendanceModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-lg max-w-4xl w-full max-h-[80vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                <h3 className="text-2xl font-bold text-gray-900">Class Attendance</h3>
                <button
                  onClick={() => setState(prev => ({ ...prev, ui: { ...prev.ui, showAttendanceModal: null, classAttendance: [] } }))}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <FiX size={24} />
                </button>
              </div>
              <div className="p-6">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Session Date</label>
                  <input
                    type="date"
                    value={state.ui.attendanceSessionDate || new Date().toISOString().split('T')[0]}
                    onChange={(e) => fetchClassAttendance(state.ui.showAttendanceModal, e.target.value)}
                    className="border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
                {state.ui.classAttendance.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500 mb-4">No students enrolled in this class</p>
                    <p className="text-sm text-gray-400">Enroll students in the class to mark attendance</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {state.ui.classAttendance.map((item, index) => {
                      const hasAttendance = item.attendance !== null;
                      const currentStatus = hasAttendance ? item.attendance.status : null;
                      // Ensure studentId is always a string
                      const studentId = item.studentId?._id 
                        ? String(item.studentId._id) 
                        : item.studentId 
                        ? String(item.studentId) 
                        : null;
                      
                      if (!studentId) {
                        console.error('Missing studentId for item:', item);
                        return null;
                      }
                      
                      return (
                        <div key={studentId || index} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">{formatName(item.studentName)}</p>
                            <p className="text-sm text-gray-500">{item.studentEmail}</p>
                        </div>
                          <div className="flex items-center gap-3">
                            {hasAttendance ? (
                              <>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                  currentStatus === 'attended' ? 'bg-green-100 text-green-800' :
                                  currentStatus === 'excused' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                                  {currentStatus === 'excused' ? 'missed' : currentStatus}
                          </span>
                                <div className="flex gap-2">
                          <button
                                    onClick={() => handleMarkAttendance(state.ui.showAttendanceModal, studentId, 'attended', state.ui.attendanceSessionDate)}
                                    className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                                      currentStatus === 'attended' 
                                        ? 'bg-green-600 text-white' 
                                        : 'bg-green-100 text-green-700 hover:bg-green-200'
                                    }`}
                                    disabled={state.ui.isLoading}
                                  >
                                    Attended
                                  </button>
                                  <button
                                    onClick={() => handleMarkAttendance(state.ui.showAttendanceModal, studentId, 'missed', state.ui.attendanceSessionDate)}
                                    className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                                      currentStatus === 'missed' || currentStatus === 'excused'
                                        ? 'bg-red-600 text-white' 
                                        : 'bg-red-100 text-red-700 hover:bg-red-200'
                                    }`}
                                    disabled={state.ui.isLoading}
                                  >
                                    Missed
                          </button>
                        </div>
                              </>
                            ) : (
                              <div className="flex items-center gap-3">
                                <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-blue-50 border border-blue-200">
                                  <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></div>
                                  <span className="text-xs font-medium text-blue-700">Pending</span>
                      </div>
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handleMarkAttendance(state.ui.showAttendanceModal, studentId, 'attended', state.ui.attendanceSessionDate)}
                                    className="px-4 py-1.5 rounded-md text-xs font-medium bg-green-600 text-white hover:bg-green-700 transition-colors shadow-sm"
                                    disabled={state.ui.isLoading}
                                  >
                                    Mark Attended
                                  </button>
                                  <button
                                    onClick={() => handleMarkAttendance(state.ui.showAttendanceModal, studentId, 'missed', state.ui.attendanceSessionDate)}
                                    className="px-4 py-1.5 rounded-md text-xs font-medium bg-red-600 text-white hover:bg-red-700 transition-colors shadow-sm"
                                    disabled={state.ui.isLoading}
                                  >
                                    Mark Missed
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Recordings Modal */}
        {state.ui.showRecordingsModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-lg max-w-4xl w-full max-h-[80vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                <h3 className="text-2xl font-bold text-gray-900">Class Recordings</h3>
                <button
                  onClick={() => setState(prev => ({ ...prev, ui: { ...prev.ui, showRecordingsModal: null, classRecordings: [] } }))}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <FiX size={24} />
                </button>
              </div>
              <div className="p-6">
                <div className="mb-4">
                  <h4 className="font-medium text-gray-900 mb-2">Add Recording Link</h4>
                  <p className="text-sm text-gray-600 mb-3">
                    Paste a link to your recording (YouTube, Google Drive, Dropbox, Vimeo, or any other video hosting service)
                  </p>
                  <div className="flex flex-col gap-2">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        id="recordingUrl"
                        placeholder="https://youtube.com/watch?v=... or https://drive.google.com/..."
                        className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                      <input
                        type="text"
                        id="recordingTitle"
                        placeholder="Recording Title (e.g., Week 1 - Hatha Yoga)"
                        className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                    </div>
                    <button
                      onClick={() => {
                        const url = document.getElementById('recordingUrl').value.trim();
                        const title = document.getElementById('recordingTitle').value.trim();
                        if (!url || !title) {
                          toast.error('Please provide both recording link and title');
                          return;
                        }
                        // Basic URL validation
                        try {
                          new URL(url);
                        } catch (e) {
                          toast.error('Please enter a valid URL (e.g., https://youtube.com/...)');
                          return;
                        }
                        handleUploadRecording(state.ui.showRecordingsModal, url, title);
                        document.getElementById('recordingUrl').value = '';
                        document.getElementById('recordingTitle').value = '';
                      }}
                      className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors flex items-center justify-center gap-2"
                    >
                      <FiUpload className="inline" />
                      Add Recording Link
                    </button>
                  </div>
                </div>
                {state.ui.classRecordings.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No recordings available</p>
                ) : (
                  <div className="space-y-3">
                    {state.ui.classRecordings.map((recording, index) => (
                      <div key={recording._id || index} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                        <div className="flex items-center gap-3">
                          <FiVideo className="text-purple-600" size={20} />
                          <div>
                            <p className="font-medium">{recording.title}</p>
                            <p className="text-sm text-gray-500">
                              {formatDateUtil(recording.uploadedAt, state.dateTimeSettings.dateFormat)}
                            </p>
                            <a
                              href={recording.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-blue-600 hover:underline"
                            >
                              View Recording
                            </a>
                          </div>
                        </div>
                        <button
                          onClick={() => setState(prev => ({ 
                            ...prev, 
                            ui: { 
                              ...prev.ui, 
                              showDeleteRecordingConfirm: { classId: state.ui.showRecordingsModal, recordingId: recording._id }
                            } 
                          }))}
                          className="text-red-600 hover:text-red-800"
                        >
                          <FiTrash2 size={18} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Delete Recording Confirmation */}
      <ConfirmDialog
        isOpen={state.ui.showDeleteRecordingConfirm !== null}
        onClose={() => setState(prev => ({ 
          ...prev, 
          ui: { ...prev.ui, showDeleteRecordingConfirm: null } 
        }))}
        onConfirm={() => {
          if (state.ui.showDeleteRecordingConfirm) {
            handleDeleteRecording(
              state.ui.showDeleteRecordingConfirm.classId, 
              state.ui.showDeleteRecordingConfirm.recordingId
            );
            setState(prev => ({ 
              ...prev, 
              ui: { ...prev.ui, showDeleteRecordingConfirm: null } 
            }));
          }
        }}
        title="Delete Recording"
        message="Are you sure you want to delete this recording? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
}

export default Classes;